/** @format */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableHead,
    TableRow,
    MenuItem,
    Select,
    CircularProgress,
    Stack,
    FormControl,
    InputLabel,
    Button,
} from "@mui/material";
import { Refresh, OpenInNew } from "@mui/icons-material";

import { selectActiveBusinessId } from "../../redux/selectors/collectSelector";
import {
    fetchManualReview,
    fetchBusinessMembers,
    assignInvoiceReviewer,
    IManualReviewItem,
    IBusinessMember,
} from "../../redux/services/collectTaskService";
import { useToast } from "../../providers/ToastProvider";
import {
    CollectPageWrapper,
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectTableContainer,
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
    CollectGhostButton,
    FlexBetween,
} from "./styles";

// ─────────────────────────────────────────────────────────────────────────
// CollectManualReview (Phase 2E) — invoices that the recovery engine has
// kicked out of automated dunning. Each row shows the customer, balance,
// days overdue, and an inline reviewer picker driven by BusinessMembership.
// ─────────────────────────────────────────────────────────────────────────

function formatMoney(amount?: number, currency = "USD"): string {
    if (typeof amount !== "number" || isNaN(amount)) return "—";
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
        }).format(amount);
    } catch {
        return `$${amount.toFixed(2)}`;
    }
}

function formatDate(v?: string): string {
    if (!v) return "—";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "—";
    try {
        return d.toLocaleDateString();
    } catch {
        return v;
    }
}

function daysSince(v?: string): number | null {
    if (!v) return null;
    const t = new Date(v).getTime();
    if (isNaN(t)) return null;
    return Math.max(0, Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24)));
}

function memberLabel(m: IBusinessMember): string {
    const first = m.firstName?.trim() || "";
    const last = m.lastName?.trim() || "";
    if (first || last) return `${first} ${last}`.trim();
    return m.email || "Unknown";
}

const CollectManualReview: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const businessId = useSelector(selectActiveBusinessId);

    const [items, setItems] = useState<IManualReviewItem[]>([]);
    const [members, setMembers] = useState<IBusinessMember[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [assigneeFilter, setAssigneeFilter] = useState<string>("any");
    const [assigning, setAssigning] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!businessId) return;
        setLoading(true);
        setError(null);
        try {
            const params: any = { limit: 100 };
            if (assigneeFilter === "me") params.assignedToUserId = "me";
            else if (assigneeFilter === "unassigned")
                params.assignedToUserId = "unassigned";
            const [page, mems] = await Promise.all([
                fetchManualReview(businessId, params),
                members.length === 0
                    ? fetchBusinessMembers(businessId)
                    : Promise.resolve(members),
            ]);
            setItems(page.items || []);
            if (mems !== members) setMembers(mems);
        } catch (err: any) {
            setError(err.message || "Could not load review queue");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId, assigneeFilter]);

    useEffect(() => {
        load();
    }, [load]);

    const handleAssign = async (invoiceId: string, userId: string | "") => {
        if (!businessId) return;
        setAssigning(invoiceId);
        try {
            await assignInvoiceReviewer(
                businessId,
                invoiceId,
                userId ? userId : null,
            );
            toast.success(userId ? "Reviewer assigned" : "Reviewer cleared");
            await load();
        } catch (err: any) {
            toast.error(err.message || "Could not assign reviewer");
        } finally {
            setAssigning(null);
        }
    };

    return (
        <CollectPageWrapper>
            <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 3 }, py: 3 }}>
                <FlexBetween sx={{ mb: 2 }}>
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 800,
                                color: "#0f172a",
                            }}
                        >
                            Manual review queue
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                color: "#64748b",
                                fontSize: "0.9rem",
                            }}
                        >
                            Invoices the system has set aside for human
                            judgment assign a reviewer and decide the next
                            step.
                        </Typography>
                    </Box>
                    <CollectGhostButton
                        startIcon={<Refresh fontSize="small" />}
                        onClick={load}
                        disabled={loading}
                    >
                        Refresh
                    </CollectGhostButton>
                </FlexBetween>

                <CollectSectionCard>
                    <CollectSectionHeader>
                        <Box>
                            <CollectSectionTitle>
                                {items.length > 0
                                    ? `${items.length} invoice${items.length === 1 ? "" : "s"} awaiting review`
                                    : "Review queue"}
                            </CollectSectionTitle>
                            <CollectSectionSubtitle>
                                Highest priority first
                            </CollectSectionSubtitle>
                        </Box>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Reviewer</InputLabel>
                            <Select
                                label="Reviewer"
                                value={assigneeFilter}
                                onChange={(e) =>
                                    setAssigneeFilter(String(e.target.value))
                                }
                            >
                                <MenuItem value="any">Anyone</MenuItem>
                                <MenuItem value="me">Assigned to me</MenuItem>
                                <MenuItem value="unassigned">
                                    Unassigned
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </CollectSectionHeader>

                    {loading ? (
                        <Box sx={{ p: 4, textAlign: "center" }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : error ? (
                        <CollectEmptyState>
                            <CollectEmptyTitle>
                                Couldn&apos;t load the queue
                            </CollectEmptyTitle>
                            <CollectEmptyHint>{error}</CollectEmptyHint>
                        </CollectEmptyState>
                    ) : items.length === 0 ? (
                        <CollectEmptyState>
                            <CollectEmptyTitle>
                                Nothing waiting for review
                            </CollectEmptyTitle>
                            <CollectEmptyHint>
                                When the system needs your call on an
                                invoice, it&apos;ll show up here.
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    ) : (
                        <CollectTableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <CollectTableHeadCell>
                                            Invoice
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Customer
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell align="right">
                                            Balance
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Due
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            In review since
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Reviewer
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell align="right">
                                            Open
                                        </CollectTableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((inv) => {
                                        const assigneeId =
                                            inv.assignedToUserId?._id || "";
                                        const sinceDays = daysSince(
                                            inv.recoveryStateEnteredAt,
                                        );
                                        return (
                                            <TableRow key={inv._id} hover>
                                                <CollectTableBodyCell>
                                                    <Typography
                                                        sx={{
                                                            fontFamily:
                                                                "'Montserrat', sans-serif",
                                                            fontWeight: 600,
                                                            fontSize: "0.85rem",
                                                            color: "#0f172a",
                                                        }}
                                                    >
                                                        #{inv.docNumber || "—"}
                                                    </Typography>
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    <Typography
                                                        sx={{
                                                            fontFamily:
                                                                "'Montserrat', sans-serif",
                                                            fontWeight: 600,
                                                            fontSize: "0.85rem",
                                                            color: "#0f172a",
                                                        }}
                                                    >
                                                        {inv.customerId
                                                            ?.displayName ||
                                                            "Customer"}
                                                    </Typography>
                                                    {inv.customerId
                                                        ?.primaryEmail && (
                                                            <Typography
                                                                sx={{
                                                                    color: "#64748b",
                                                                    fontSize:
                                                                        "0.75rem",
                                                                }}
                                                            >
                                                                {
                                                                    inv.customerId
                                                                        .primaryEmail
                                                                }
                                                            </Typography>
                                                        )}
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell align="right">
                                                    <Typography
                                                        sx={{
                                                            fontFamily:
                                                                "'Montserrat', sans-serif",
                                                            fontWeight: 700,
                                                            color: "#0f172a",
                                                        }}
                                                    >
                                                        {formatMoney(
                                                            inv.balance,
                                                            inv.currency,
                                                        )}
                                                    </Typography>
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    {formatDate(inv.dueDate)}
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    {sinceDays === null
                                                        ? "—"
                                                        : sinceDays === 0
                                                            ? "today"
                                                            : `${sinceDays} day${sinceDays === 1 ? "" : "s"} ago`}
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    <FormControl
                                                        size="small"
                                                        sx={{ minWidth: 160 }}
                                                    >
                                                        <Select
                                                            value={assigneeId}
                                                            displayEmpty
                                                            disabled={
                                                                assigning ===
                                                                inv._id
                                                            }
                                                            onChange={(e) =>
                                                                handleAssign(
                                                                    inv._id,
                                                                    String(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                        >
                                                            <MenuItem value="">
                                                                <em>
                                                                    Unassigned
                                                                </em>
                                                            </MenuItem>
                                                            {members.map((m) => (
                                                                <MenuItem
                                                                    key={
                                                                        m.userId
                                                                    }
                                                                    value={
                                                                        m.userId
                                                                    }
                                                                >
                                                                    {memberLabel(
                                                                        m,
                                                                    )}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell align="right">
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        startIcon={
                                                            <OpenInNew fontSize="small" />
                                                        }
                                                        onClick={() =>
                                                            navigate(
                                                                `/collect/invoices/${inv._id}`,
                                                            )
                                                        }
                                                    >
                                                        Open
                                                    </Button>
                                                </CollectTableBodyCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </CollectTableContainer>
                    )}
                </CollectSectionCard>
            </Box>
        </CollectPageWrapper>
    );
};

export default CollectManualReview;
